import { Link } from "@tanstack/react-router";
import {
	BarChart3,
	Download,
	GitCompare,
	History,
	Home,
	Key,
	Menu,
	Settings,
	User,
	X,
} from "lucide-react";
import { useState } from "react";
import { useAppAuth } from "../hooks/useAppAuth";
import { getUserAvatarUrl, getUserDisplayName } from "../lib/user-profile";
import AuthButton from "./AuthButton";
import { SettingsModal } from "./SettingsModal";

/**
 * Renders the application header with controls and a slide-in left navigation menu.
 *
 * The header includes a menu button, title link, and a quick settings button that opens the settings modal.
 * The left aside is a sliding navigation panel with links for Home, Search History, Compare Searches, Export Datasets, and Settings; selecting a link closes the menu.
 *
 * @returns The header and left-side navigation menu as JSX elements
 */
export default function Header() {
	const [isOpen, setIsOpen] = useState(false);
	const [showSettings, setShowSettings] = useState(false);
	const { user, isAuthenticated, isLoading, isAnonymous } = useAppAuth();
	const avatarUrl = getUserAvatarUrl(user);
	const displayName = getUserDisplayName(user);
	const initials = displayName.charAt(0).toUpperCase();

	return (
		<>
			<header className="p-4 flex items-center justify-between bg-gray-800 text-white shadow-lg">
				<div className="flex items-center">
					<button
						type="button"
						onClick={() => setIsOpen(true)}
						className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
						aria-label="Open menu"
					>
						<Menu size={24} />
					</button>
					<h1 className="ml-4 text-xl font-semibold">
						<Link to="/" className="hover:text-primary-400 transition-colors">
							Agentic Search
						</Link>
					</h1>
				</div>

				<div className="flex items-center gap-3">
					{/* Quick Settings Button */}
					<button
						type="button"
						onClick={() => setShowSettings(true)}
						className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors"
						aria-label="Configure API Keys"
					>
						<Key size={18} />
						<span className="font-medium">Configure Models</span>
					</button>

					{/* Auth Button */}
					<AuthButton />
				</div>
			</header>

			{/* Settings Modal */}
			<SettingsModal
				isOpen={showSettings}
				onClose={() => setShowSettings(false)}
			/>

			<aside
				className={`fixed top-0 left-0 h-full w-80 bg-gray-900 text-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${
					isOpen ? "translate-x-0" : "-translate-x-full"
				}`}
			>
				<div className="flex items-center justify-between p-4 border-b border-gray-700">
					<h2 className="text-xl font-bold">Menu</h2>
					<button
						type="button"
						onClick={() => setIsOpen(false)}
						className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
						aria-label="Close menu"
					>
						<X size={24} />
					</button>
				</div>

				<nav className="flex-1 p-4 overflow-y-auto">
					<Link
						to="/"
						onClick={() => setIsOpen(false)}
						className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-800 transition-colors mb-2"
						activeProps={{
							className:
								"flex items-center gap-3 p-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 transition-colors mb-2",
						}}
					>
						<Home size={20} />
						<span className="font-medium">Home</span>
					</Link>

					<Link
						to="/history"
						onClick={() => setIsOpen(false)}
						className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-800 transition-colors mb-2"
						activeProps={{
							className:
								"flex items-center gap-3 p-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 transition-colors mb-2",
						}}
					>
						<History size={20} />
						<span className="font-medium">Search History</span>
					</Link>

					<Link
						to="/comparison"
						onClick={() => setIsOpen(false)}
						className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-800 transition-colors mb-2"
						activeProps={{
							className:
								"flex items-center gap-3 p-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 transition-colors mb-2",
						}}
					>
						<GitCompare size={20} />
						<span className="font-medium">Compare Searches</span>
					</Link>

					<Link
						to="/export"
						onClick={() => setIsOpen(false)}
						className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-800 transition-colors mb-2"
						activeProps={{
							className:
								"flex items-center gap-3 p-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 transition-colors mb-2",
						}}
					>
						<Download size={20} />
						<span className="font-medium">Export Datasets</span>
					</Link>

					<Link
						to="/profile"
						onClick={() => setIsOpen(false)}
						className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-800 transition-colors mb-2"
						activeProps={{
							className:
								"flex items-center gap-3 p-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 transition-colors mb-2",
						}}
					>
						<User size={20} />
						<span className="font-medium">Profile</span>
					</Link>

					<Link
						to="/analytics"
						onClick={() => setIsOpen(false)}
						className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-800 transition-colors mb-2"
						activeProps={{
							className:
								"flex items-center gap-3 p-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 transition-colors mb-2",
						}}
					>
						<BarChart3 size={20} />
						<span className="font-medium">Analytics</span>
					</Link>

					<Link
						to="/settings"
						onClick={() => setIsOpen(false)}
						className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-800 transition-colors mb-2"
						activeProps={{
							className:
								"flex items-center gap-3 p-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 transition-colors mb-2",
						}}
					>
						<Settings size={20} />
						<span className="font-medium">Settings</span>
					</Link>
				</nav>

				<div className="p-4 border-t border-gray-700 bg-gray-800 flex flex-col gap-3">
					{!isLoading && (
						<div className="rounded-lg border border-gray-700 bg-gray-900/70 p-3">
							{isAuthenticated ? (
								<div className="flex items-center gap-3">
									{avatarUrl ? (
										<img
											src={avatarUrl}
											alt={displayName}
											className="w-10 h-10 rounded-full border border-cyan-500"
										/>
									) : (
										<div className="w-10 h-10 rounded-full bg-cyan-600 flex items-center justify-center text-sm font-bold text-white">
											{initials}
										</div>
									)}
									<div className="min-w-0">
										<div className="text-sm font-medium text-white truncate">
											{displayName}
										</div>
										<div className="text-xs text-gray-400 truncate">
											{isAnonymous ? "Guest session" : user?.email || "Signed in"}
										</div>
									</div>
								</div>
							) : (
								<div className="text-sm text-gray-400">
									Sign in from the top-right account button to sync your data.
								</div>
							)}
						</div>
					)}
					<div className="text-sm text-gray-400">Secure Agentic Search</div>
				</div>
			</aside>
		</>
	);
}
